import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { DefaultDocumentTypeIds, DocumentType } from 'common/models/Document';
import CreateDocumentTypeDialog from 'components/Dialogs/Documents/CreateDocumentTypeDialog';
import EditDocumentTypeDialog from 'components/Dialogs/Documents/EditDocumentTypeDialog';
import GridWithStateWrapper from 'components/GridWidthStateWrapper';
import TitleAndActionSummaryBar from 'components/SummaryBars/TitleAndActionSummaryBar';
import PageContentLayout from 'layouts/PageContentLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetAllDocumentTypes } from 'services/DocumentsService';

interface Props {
  setSummaryBar?: (summaryBar: JSX.Element) => void;
}

const defaultColsVisibility: GridColumnVisibilityModel = {
  id: false,
};

export default function DocumentTypesPage({ setSummaryBar }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [rows, setRows] = useState<DocumentType[]>([]);
  const [editRowData, setEditRowData] = useState<DocumentType | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [defaultDocumentTypeId, setDefaultDocumentTypeId] = useState(0);
  const [defaultFormattedCVTypeId, setDefaultFormattedCVTypeId] = useState(0);
  const [defaultSubmittedCVTypeId, setDefaultSubmittedCVTypeId] = useState(0);
  const [defaultReferenceCheckypeId, setDefaultReferenceCheckypeId] = useState(0);
  const apiRef = useGridApiRef();

  const summaryBar = useMemo(() => {
    const action = (
      <Button color="success" variant="contained" onClick={() => setShowCreateDialog(true)}>
        Create New Type
      </Button>
    );
    return (
      <TitleAndActionSummaryBar
        title="Configuration > Document Types"
        browserTabTitle="Document Types > Configuration"
        action={action}
      />
    );
  }, []);

  useEffect(() => {
    setSummaryBar?.(summaryBar);
  }, [setSummaryBar, summaryBar]);

  const getData = useCallback(async () => {
    setIsLoading(true);
    const res = await GetAllDocumentTypes(setErrorMessage);
    if (res) {
      let defaultDocType = 0;
      let defaultFormattedCv = 0;
      let defaultSubmittedCv = 0;
      let defaultRefCheck = 0;
      for (let i = 0; i < res.length; i++) {
        const t = res[i];
        if (t.defaultDocumentType) defaultDocType = t.id;
        if (t.defaultFormattedCVType) defaultFormattedCv = t.id;
        if (t.defaultSubmittedCVType) defaultSubmittedCv = t.id;
        if (t.defaultReferenceCheckType) defaultRefCheck = t.id;
      }
      setRows(res);
      setDefaultDocumentTypeId(defaultDocType);
      setDefaultFormattedCVTypeId(defaultFormattedCv);
      setDefaultSubmittedCVTypeId(defaultSubmittedCv);
      setDefaultReferenceCheckypeId(defaultRefCheck);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo<GridColDef[]>(() => {
    const openEditDialogRenderer = (params: GridRenderCellParams<DocumentType>) => {
      return (
        <span
          onClick={() => setEditRowData(params.row)}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          {params.value}
        </span>
      );
    };

    return [
      { field: 'id', headerName: 'ID', renderCell: openEditDialogRenderer, width: 150 },
      { field: 'name', headerName: 'Name', renderCell: openEditDialogRenderer, width: 300 },
      { field: 'isClientsEnabled', headerName: 'Clients', type: 'boolean' },
      { field: 'isContactsEnabled', headerName: 'Contacts', type: 'boolean' },
      { field: 'isCandidatesEnabled', headerName: 'Candidates', type: 'boolean' },
      { field: 'isJobsEnabled', headerName: 'Jobs', type: 'boolean' },
      { field: 'isPlacementsEnabled', headerName: 'Placements', type: 'boolean' },
      { field: 'isOpportunitiesEnabled', headerName: 'Opportunities', type: 'boolean', width: 150 },
      { field: 'isSitesEnabled', headerName: 'Sites', type: 'boolean' },
    ];
  }, []);

  const editSuccessHandler = useCallback(
    (message: string, newData: DocumentType) => {
      setSuccessMessage(message);
      if (
        newData.defaultDocumentType ||
        newData.defaultFormattedCVType ||
        newData.defaultSubmittedCVType ||
        newData.defaultReferenceCheckType
      ) {
        getData();
      } else {
        const api = apiRef.current;
        if (api) {
          api.updateRows([newData]);
        }
      }
    },
    [apiRef, getData]
  );

  const creationSuccessHandler = useCallback(
    (message: string, newRow: DocumentType) => {
      setSuccessMessage(message);
      if (
        newRow.defaultDocumentType ||
        newRow.defaultFormattedCVType ||
        newRow.defaultSubmittedCVType
      ) {
        getData();
      } else {
        setRows((prev) => {
          const tmp = [...prev];
          tmp.push(newRow);
          return tmp;
        });
      }
    },
    [getData]
  );

  const defaultTypeArrays = useMemo<DefaultDocumentTypeIds>(
    () => ({
      documentType: defaultDocumentTypeId,
      formattedCvType: defaultFormattedCVTypeId,
      submittedCvType: defaultSubmittedCVTypeId,
      referenceCheck: defaultReferenceCheckypeId,
    }),
    [
      defaultDocumentTypeId,
      defaultFormattedCVTypeId,
      defaultSubmittedCVTypeId,
      defaultReferenceCheckypeId,
    ]
  );

  return (
    <>
      <Snackbar open={Boolean(errorMessage)}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert color="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
      <EditDocumentTypeDialog
        open={Boolean(editRowData)}
        data={editRowData}
        defaultTypeIds={defaultTypeArrays}
        closeHandler={() => setEditRowData(null)}
        loadingHandler={setIsLoading}
        errorHandler={setErrorMessage}
        successHandler={editSuccessHandler}
      />
      <CreateDocumentTypeDialog
        open={showCreateDialog}
        closeHandler={() => setShowCreateDialog(false)}
        loadingHandler={setIsLoading}
        errorHandler={setErrorMessage}
        successHandler={creationSuccessHandler}
      />
      <PageContentLayout title="Document Types" showLoading={isLoading}>
        <GridWithStateWrapper
          gridName="configuration/document-types"
          defaultViewModel={defaultColsVisibility}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          pagination
          pageSizeOptions={[100, 250, 500, 1000]}
        />
      </PageContentLayout>
    </>
  );
}
